'use client';

import type { CouponGoodsItemDto, GetCouponGoodsServiceResponse } from '@musinsa/services-curator';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { twMerge } from 'tailwind-merge';
import { curatorQueries } from '../../query-factory';
import { useScrollDirection } from '../hooks';
import { CouponGoodsCard } from './CouponGoodsCard';
import { CouponGoodsCategoryTabs } from './CouponGoodsCategoryTabs';
import { type CouponGoodsSort, CouponGoodsSortMenu } from './CouponGoodsSortMenu';
import { EmptyCouponGoodsView } from './EmptyCouponGoodsView';

interface CouponGoodsListProps {
  couponNo: number;
  /** 판매자 본인 여부. CouponGoodsCard 의 판매수/engagement 분기에 사용. */
  isOwner: boolean;
  /** GA4 view_item_list / select_item ecommerce attr 빌드용 메타. page 가 isMusinsa 일 때만 주입. */
  coupon?: { couponId: number; couponName: string };
  /** PLP 상품 노출 콜백 (impression_content). page 가 isMusinsa 일 때만 주입. */
  onProductImpression?: (item: CouponGoodsItemDto) => void;
  /** PLP 상품 클릭 콜백 (click_product / click_content). page 가 isMusinsa 일 때만 주입. */
  onClickProduct?: (item: CouponGoodsItemDto) => void;
  className?: string;
}

const COLUMN_COUNT = 3;

const chunkArray = <T,>(arr: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

/**
 * 쿠폰 적용 상품 페이지의 PLP 영역. 카테고리 탭 + 정렬 메뉴 + 3-col 상품 그리드.
 *
 * 가상화: 다른 curator PLP (CurationList, VirtualizedLikedProductList 등) 와 동일하게
 * react-virtuoso 의 `<Virtuoso useWindowScroll />` 패턴을 사용한다. 3-col 그리드는
 * `chunkArray` 로 행 단위 묶어 itemContent 에서 grid 로 렌더.
 *
 * 스크롤 동작:
 * - 카테고리 탭은 항상 sticky (top:0). 정렬 행과 같은 sticky 컨테이너에 들어있어
 *   둘 사이의 매직 넘버 의존 없이 정확히 붙는다.
 * - 정렬 행은 스크롤 다운 시 max-height 0 으로 접혀 layout 까지 함께 줄어든다.
 *
 * 무한 스크롤: Virtuoso 의 `endReached` 콜백으로 `fetchNextPage` 트리거.
 */
export const CouponGoodsList = ({
  couponNo,
  isOwner,
  coupon,
  onProductImpression,
  onClickProduct,
  className,
}: CouponGoodsListProps) => {
  const [selectedCategoryCode, setSelectedCategoryCode] = useState<string | undefined>();
  const [selectedSort, setSelectedSort] = useState<CouponGoodsSort>('POPULAR');

  const scrollDirection = useScrollDirection();
  const queryClient = useQueryClient();

  // 탭/정렬 변경 시 이전 페이지 캐시를 제거하고 스크롤을 상단으로 reset.
  // 그대로 두면 새 리스트의 첫 화면을 못 보고 이전 위치(예: 800px 아래)에 머무름.
  const resetListView = useCallback(() => {
    queryClient.removeQueries({ queryKey: curatorQueries.coupon.goodsInfinite._def });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [queryClient]);

  const handleSelectCategory = useCallback(
    (code: string | undefined) => {
      if (code === selectedCategoryCode) return;
      setSelectedCategoryCode(code);
      resetListView();
    },
    [selectedCategoryCode, resetListView],
  );

  const handleSelectSort = useCallback(
    (sort: CouponGoodsSort) => {
      if (sort === selectedSort) return;
      setSelectedSort(sort);
      resetListView();
    },
    [selectedSort, resetListView],
  );

  const { data: categoriesData } = useQuery(curatorQueries.coupon.goodsCategories({ couponNo }));
  const { data: countData } = useQuery(
    curatorQueries.coupon.goodsCount({ couponNo, categoryCode: selectedCategoryCode }),
  );

  const {
    data: goodsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isSuccess,
  } = useInfiniteQuery({
    ...curatorQueries.coupon.goodsInfinite({
      couponNo,
      categoryCode: selectedCategoryCode,
      sort: selectedSort,
    }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: GetCouponGoodsServiceResponse) =>
      lastPage.pagination.hasNextPage ? lastPage.pagination.nextPage : undefined,
  });

  const items = useMemo(() => goodsData?.pages.flatMap((page) => page.list) ?? [], [goodsData]);
  const rows = useMemo(() => chunkArray(items, COLUMN_COUNT), [items]);
  // 첫 페이지가 도착했고 (`isSuccess`) 결과 list 가 0건일 때만 empty view 노출.
  // 초기 로딩 중에는 items 가 빈 배열이라도 empty view 가 깜빡이지 않도록 isSuccess 게이트.
  const isEmpty = isSuccess && items.length === 0;

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const renderRow = useCallback(
    (index: number) => {
      const row = rows[index];
      // NOTE: react-virtuoso "Zero-sized element" 경고 방지를 위해 빈 placeholder 반환
      if (!row) return <div style={{ minHeight: 1 }} />;
      const rowStart = index * COLUMN_COUNT;
      return (
        <div className="grid grid-cols-3 bg-white">
          {row.map((item, colIdx) => (
            <CouponGoodsCard
              key={item.goodsNo}
              item={item}
              index={rowStart + colIdx}
              coupon={coupon}
              isOwner={isOwner}
              onImpression={onProductImpression}
              onClickProduct={onClickProduct}
            />
          ))}
          {Array.from({ length: COLUMN_COUNT - row.length }).map((_, emptyIdx) => (
            <div key={`empty-${emptyIdx}`} />
          ))}
        </div>
      );
    },
    [rows, isOwner, coupon, onProductImpression, onClickProduct],
  );

  const computeItemKey = useCallback(
    (index: number) => {
      const row = rows[index];
      if (!row) return `row-${index}`;
      return row.map((item) => item.goodsNo).join('_');
    },
    [rows],
  );

  const isSortHidden = scrollDirection === 'down';

  // 무신사 공통 appbar(`#commonLayoutHeader`)는 sticky 로 viewport 상단을 차지한다.
  // PC 는 56(글로벌 GNB) + 52(appbar) = 108, 모바일은 0 + 52 등 환경마다 다르므로
  // CSS var 대신 실제 element 의 bottom 을 측정해 sticky top 으로 적용한다.
  // appbar 자체가 sticky 라 scroll 위치와 무관하게 bottom 이 고정되므로 scroll listener
  // 는 두지 않는다 (매 scroll setState → flicker 원인).
  const [stickyTop, setStickyTop] = useState(0);
  useEffect(() => {
    const appbar = document.getElementById('commonLayoutHeader');
    if (!appbar) return;
    const update = () => {
      const bottom = appbar.getBoundingClientRect().bottom;
      setStickyTop(bottom > 0 ? bottom : 0);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(appbar);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div
      className={twMerge(
        'flex grow flex-col bg-gray-100',
        // floating button (ScrollToTopButton) 가 마지막 상품 카드를 가리지 않도록 buffer 확보.
        // empty 일 땐 카드 자체가 없어 buffer 불필요.
        isEmpty ? null : 'pb-[72px]!',
        className,
      )}
    >
      <div className="sticky z-20 bg-gray-100" style={{ top: stickyTop }}>
        <CouponGoodsCategoryTabs
          categories={categoriesData?.list ?? []}
          selectedCategoryCode={selectedCategoryCode}
          onSelect={handleSelectCategory}
        />
        <div
          aria-hidden={isSortHidden}
          className={twMerge(
            'transition-[max-height,opacity] duration-200 ease-out',
            // @musinsa/mds Menu 는 FloatingPortal 미사용 — MenuContent 가 같은 부모 트리에
            // position:absolute 로 렌더된다. 펼침 상태에선 overflow:visible 로 두어야
            // 드롭다운 패널이 부모 wrapper 의 max-h 박스 밖으로 정상 그려진다 (QAT-38806).
            isSortHidden
              ? 'pointer-events-none max-h-0 opacity-0 overflow-hidden'
              : 'max-h-[60px] opacity-100 overflow-visible',
          )}
        >
          <CouponGoodsSortMenu
            totalCount={countData?.totalCount}
            selectedSort={selectedSort}
            onSelect={handleSelectSort}
          />
        </div>
      </div>
      {isEmpty ? (
        <EmptyCouponGoodsView />
      ) : (
        <Virtuoso
          useWindowScroll
          totalCount={rows.length}
          itemContent={renderRow}
          computeItemKey={computeItemKey}
          endReached={handleEndReached}
          overscan={2}
          increaseViewportBy={{ top: 500, bottom: 500 }}
        />
      )}
    </div>
  );
};
